import { useState, useRef, useEffect } from 'react';
import { Printer, Pencil, X, Save, Loader } from 'lucide-react';
import apiService from '../config';
import { toast } from 'react-toastify';

// Helper function to convert number to Marathi text
const numberToMarathiWords = (num) => {
  if (!num || isNaN(num)) return "शून्य";

  const ones = [
    "", "एक", "दोन", "तीन", "चार", "पाच", "सहा", "सात", "आठ", "नऊ",
    "दहा", "अकरा", "बारा", "तेरा", "चौदा", "पंधरा", "सोळा", "सतरा", "अठरा", "एकोणीस"
  ];

  const traditionalNumbers = {
    20: "वीस", 21: "एकवीस", 22: "बावीस", 23: "तेवीस", 24: "चोवीस", 25: "पंचवीस",
    26: "सव्वीस", 27: "सत्तावीस", 28: "अट्ठावीस", 29: "एकोणतीस",
    30: "तीस", 31: "एकतीस", 32: "बत्तीस", 33: "तेहतीस", 34: "चौतीस", 35: "पस्तीस",
    36: "छत्तीस", 37: "सदतीस", 38: "अडतीस", 39: "एकोणचाळीस",
    40: "चाळीस", 41: "एक्केचाळीस", 42: "बेचाळीस", 43: "त्रेचाळीस", 44: "चव्वेचाळीस", 45: "पंचेचाळीस",
    46: "सेहेचाळीस", 47: "सत्तेचाळीस", 48: "अट्ठेचाळीस", 49: "एकोणपन्नास",
    50: "पन्नास", 51: "एक्कावन्न", 52: "बावन्न", 53: "त्रेपन्न", 54: "चौपन्न", 55: "पंचावन्न",
    56: "छप्पन्न", 57: "सत्तावन्न", 58: "अट्ठावन्न", 59: "एकोणसाठ",
    60: "साठ", 61: "एकसष्ट", 62: "बासष्ट", 63: "त्रेसष्ट", 64: "चौसष्ट", 65: "पासष्ट",
    66: "सहासष्ट", 67: "सदुसष्ट", 68: "अडुसष्ट", 69: "एकोणसत्तर",
    70: "सत्तर", 71: "एकाहत्तर", 72: "बाहत्तर", 73: "त्र्याहत्तर", 74: "चौऱ्याहत्तर", 75: "पंच्याहत्तर",
    76: "शहात्तर", 77: "सत्याहत्तर", 78: "अठ्ठ्याहत्तर", 79: "एकोणऐंशी",
    80: "ऐंशी", 81: "एक्याऐंशी", 82: "ब्याऐंशी", 83: "त्र्याऐंशी", 84: "चौऱ्याऐंशी", 85: "पंच्याऐंशी",
    86: "शहाऐंशी", 87: "सत्याऐंशी", 88: "अठ्ठ्याऐंशी", 89: "एकोणनव्वद",
    90: "नव्वद", 91: "एक्याण्णव", 92: "ब्याण्णव", 93: "त्र्याण्णव", 94: "चौऱ्याण्णव", 95: "पंच्याण्णव",
    96: "शहाण्णव", 97: "सत्याण्णव", 98: "अठ्ठ्याण्णव", 99: "नव्याण्णव"
  };

  const numToWordsBelow100 = (n) => {
    if (n < 20) return ones[n];
    return traditionalNumbers[n] || "";
  };

  const numToWords = (n) => {
    if (n === 0) return "शून्य";
    let result = "";

    if (Math.floor(n / 10000000) > 0) {
      result += numToWords(Math.floor(n / 10000000)) + " कोटी ";
      n %= 10000000;
    }

    if (Math.floor(n / 100000) > 0) {
      result += numToWords(Math.floor(n / 100000)) + " लाख ";
      n %= 100000;
    }

    if (Math.floor(n / 1000) > 0) {
      result += numToWords(Math.floor(n / 1000)) + " हजार ";
      n %= 1000;
    }

    if (Math.floor(n / 100) > 0) {
      const hundreds = Math.floor(n / 100);
      if (hundreds === 1) result += "शंभर ";
      else if (hundreds === 2) result += "दोनशे ";
      else if (hundreds === 3) result += "तीनशे ";
      else if (hundreds === 4) result += "चारशे ";
      else if (hundreds === 5) result += "पाचशे ";
      else if (hundreds === 6) result += "सहाशे ";
      else if (hundreds === 7) result += "सातशे ";
      else if (hundreds === 8) result += "आठशे ";
      else if (hundreds === 9) result += "नऊशे ";
      else result += numToWords(hundreds) + "शे ";
      n %= 100;
    }

    if (n > 0) {
      result += numToWordsBelow100(n);
    }

    return result.trim();
  };

  return numToWords(parseInt(num, 10));
};

const FormC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [compensationData, setCompensationData] = useState([]);
  
  // Project Info
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);
  const [currentLaoId, setCurrentLaoId] = useState(null);
  const [proposalNo, setProposalNo] = useState('');
  const [laoName, setLaoName] = useState('');
  
  // Modal Edit State
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedOwnerIndex, setSelectedOwnerIndex] = useState(null);
  const [formData, setFormData] = useState({
    owner_name: '',
    namuna_k_date: '',
    namuna_k_default_content: '',
    namuna_k_witness1: '',
    namuna_k_witness2: ''
  });

  const documentRef = useRef(null);

  useEffect(() => {
    const getProjectInfo = () => {
      const selectedProjectData = localStorage.getItem('selectedProject');
      const currentProposal = localStorage.getItem('userData');

      if (selectedProjectData) {
        const selectedProject = JSON.parse(selectedProjectData);
        setCurrentProjectId(selectedProject.id || selectedProject.project_id || selectedProject.PROJECT_ID);
      }

      if (currentProposal) {
        const selectedProposal = JSON.parse(currentProposal);
        setCurrentProposalId(selectedProposal.default_proposal || selectedProposal.proposal_id);
        setCurrentLaoId(selectedProposal.usr_id);
      }
    };
    getProjectInfo();
  }, []);

  // Fetch Proposal Number and LAO details
  useEffect(() => {
    const fetchAdditionalDetails = async () => {
      if (!currentProjectId || !currentProposalId) return;
      try {
        const result = await apiService.getProposalPublish(currentProjectId, currentProposalId);
        if (result.status === 200 && result.data) {
          setProposalNo(result.data.section_11_bhusampadan_no || result.data.acquisition_proposal_no || '-----------');
        }

        const role = 3; // LAO Role
        const laoRes = await apiService.getAssignLAO(role, currentProjectId, currentProposalId);
        if (laoRes.status === 200 && laoRes.data && laoRes.data.length > 0) {
          setLaoName(laoRes.data[0].full_name || 'भूसंपादन अधिकारी');
        }
      } catch (error) {
        console.error('Error fetching details:', error);
      }
    };
    fetchAdditionalDetails();
  }, [currentProjectId, currentProposalId]);

  // Fetch KJP / Namuna K Data
  useEffect(() => {
    const fetchNamunaKData = async () => {
      if (!currentProjectId || !currentProposalId) return;

      setLoading(true);
      try {
        const response = await apiService.getKJPTabapavti(currentProjectId, currentProposalId);
        const dataArray = Array.isArray(response?.data || response) ? (response?.data || response) : [];
        setCompensationData(dataArray.filter(owner => owner.owner_name));
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load records.");
      } finally {
        setLoading(false);
      }
    };

    fetchNamunaKData();
  }, [currentProjectId, currentProposalId]);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '1900-01-01T00:00:00.000Z') return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return numericAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ---------------- Handlers ----------------

  const handleEditClick = (index) => {
    const record = compensationData[index];
    setSelectedOwnerIndex(index);

    const amountInWords = numberToMarathiWords(record.total_compensation);
    const formattedDate = record.namuna_k_date ? formatDate(record.namuna_k_date) : "------------";
    const formattedComp = formatCurrency(record.total_compensation);

    // Create the default string if paragraph is not previously saved
    const defaultParagraph = record.namuna_k_default_content || 
      `मी / आम्ही, खालील सही करणार पावती लिहून देतो की, भूसंपादन प्रकरण क्रमांक ${proposalNo} मा.जिल्हाधिकारी बुलडाणा यामध्ये दिनांक ${formattedDate} रोजीच्या अंतिम निवाडयानुसार संपादीत होणाऱ्या खालीलप्रमाणे आमच्या जमिनीचा त्यामधील संलग्न सर्व बाबीच्या मुल्यांकनावरील मोबदला रक्कम रु. ${formattedComp} /- (अक्षरी ${amountInWords} फक्त) मला / आम्हाला मिळाले. त्याबाबत माझी/आमची काहीही तक्रार राहणार नाही. सदरची रक्कम मी / आम्ही निषेधासह / निषेधाशिवाय स्विकारीत आहे.`;

    setFormData({
      owner_name: record.owner_name,
      namuna_k_date: record.namuna_k_date ? record.namuna_k_date.split('T')[0] : '',
      namuna_k_default_content: defaultParagraph,
      namuna_k_witness1: record.namuna_k_witness1 || '',
      namuna_k_witness2: record.namuna_k_witness2 || ''
    });

    setOpenModal(true);
  };

  const handleSave = async () => {
    if (!currentProjectId || !currentProposalId) {
      toast.error("Project / Proposal ID missing");
      return;
    }

    try {
      setSaving(true);
      const owner = compensationData[selectedOwnerIndex];

      const payload = {
        project_id: currentProjectId,
        proposal_id: currentProposalId,
        owner_id: owner.land_owner_id || owner.owner_id,
        namuna_k_date: formData.namuna_k_date,
        namuna_k_default_content: formData.namuna_k_default_content,
        namuna_k_witness1: formData.namuna_k_witness1,
        namuna_k_witness2: formData.namuna_k_witness2,
      };

      const response = await apiService.postAddUpdateKJPTabapavti(payload);

      if (response.status === 200 || response.status === 201) {
        const updatedList = compensationData.map((item, idx) => {
          if (idx !== selectedOwnerIndex) return item;
          return {
            ...item,
            namuna_k_date: formData.namuna_k_date,
            namuna_k_default_content: formData.namuna_k_default_content,
            namuna_k_witness1: formData.namuna_k_witness1,
            namuna_k_witness2: formData.namuna_k_witness2,
          };
        });

        setCompensationData(updatedList);
        setOpenModal(false);
        toast.success("नमुना - क यशस्वीपणे जतन झाला");
      }
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("जतन करताना त्रुटी आली");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const printContent = documentRef.current?.innerHTML;
    if (!printContent) {
      toast.warning("No content available to print.");
      return;
    }

    const win = window.open("", "", "height=900,width=1200");
    win.document.write(`
      <html>
        <head>
          <title>Namuna K - Land Acquisition</title>
          <style>
            @page {
              size: A4 portrait;   
              margin: 1.2cm 1.5cm;
            }
            body {
              font-family: 'DVOT-Surekh', 'Mangal', 'Noto Serif Devanagari', 'Arial', sans-serif;
              font-size: 15px;
              line-height: 1.6;
              color: #000;
              margin: 0;
              padding: 0;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-bold { font-weight: bold; }
            .border { border: 1px solid black; }
            .border-collapse { border-collapse: collapse; }
            table { width: 100%; margin: 20px 0; border-collapse: collapse; }
            th, td { border: 1px solid black; padding: 8px; text-align: center; vertical-align: middle; }
            th { font-weight: bold; }
            .para { text-align: justify; margin: 15px 0; text-indent: 40px; }
            .no-print, button { display: none !important; }
            .certificate { page-break-after: always; padding: 20px; }
            .certificate:last-child { page-break-after: auto; }
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 800);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
           <Loader className="h-10 w-10 animate-spin text-blue-600" />
           <p className="text-gray-500 font-medium">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-10">
      {/* Top Bar for Action Buttons */}
      <div className="no-print flex justify-end gap-3 p-4 bg-white shadow-sm border-b ">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white font-medium rounded-lg shadow hover:bg-green-700 transition-colors"
        >
          <Printer size={18} /> Print All (नमुना - क)
        </button>
      </div>

      <div ref={documentRef} className="max-w-4xl mx-auto mt-6">
        {compensationData && compensationData.length > 0 ? (
          compensationData.map((owner, index) => {
            const aqrArea = parseFloat(owner.owner_aqr_area) || 0;
            const totalArea = parseFloat(owner.owner_total_area) || 0;
            const potKharab = Math.max(0, totalArea - aqrArea);
            const amountInWords = numberToMarathiWords(owner.total_compensation);

            return (
              <div 
                key={owner.tabapavti_id || index} 
                className="certificate bg-white shadow-2xl border border-gray-200 mb-10 mx-4 sm:mx-0 p-10 relative"
              >
                {/* Edit Button - Hidden in Print */}
                <div className="no-print absolute top-6 right-6">
                  <button
                    onClick={() => handleEditClick(index)}
                    className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm transition-colors shadow-sm"
                  >
                    <Pencil size={14} /> संपादित करा (Edit)
                  </button>
                </div>

                <div className="text-right text-sm font-bold mb-2">
                  अ.क्र. {index + 1}
                </div>

                <div className="text-center font-bold text-2xl mb-8 underline decoration-2 underline-offset-4">
                  नमुना - क
                </div>

                <div className="flex justify-between  mb-6 text-[15px]">
                  <div>लिहून घेणार :-  महाराष्ट्र शासन तर्फे (भूसंपादन अधिकारी)</div>
                </div>
                <div className="flex justify-between  mb-6 text-[15px]">
                  <div>पावती लिहून देणार :- {owner.owner_name}</div>
                </div>

                <div className="text-center text-xl  tracking-widest my-4">
                  *****
                </div>

                {/* Show edited content if available, else show default formatted paragraph */}
                {owner.namuna_k_default_content ? (
                  <p className="para text-[15px] leading-relaxed whitespace-pre-line">
                    {owner.namuna_k_default_content}
                  </p>
                ) : (
                  <p className="para text-[15px] leading-relaxed">
                    मी / आम्ही, खालील सही करणार पावती लिहून देतो की, भूसंपादन प्रकरण क्रमांक {proposalNo} मा.जिल्हाधिकारी बुलडाणा यामध्ये दिनांक {owner.namuna_k_date ? formatDate(owner.namuna_k_date) : "------------"} रोजीच्या अंतिम निवाडयानुसार संपादीत होणाऱ्या खालीलप्रमाणे आमच्या जमिनीचा त्यामधील संलग्न सर्व बाबीच्या मुल्यांकनावरील मोबदला रक्कम रु. {formatCurrency(owner.total_compensation)} /- (अक्षरी {amountInWords} फक्त) मला / आम्हाला मिळाले. त्याबाबत माझी/आमची काहीही तक्रार राहणार नाही. सदरची रक्कम मी / आम्ही निषेधासह / निषेधाशिवाय स्विकारीत आहे.
                  </p>
                )}

                <table className="w-full border-collapse border border-black my-6">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="border border-black p-2" rowSpan="2">गावाचे नाव व तालुका, जिल्हा</th>
                      <th className="border border-black p-2" rowSpan="2">गट नं.</th>
                      <th className="border border-black p-2" colSpan="3">संपादीत क्षेत्र</th>
                      <th className="border border-black p-2" rowSpan="2">50% अग्रीम मोबदला रक्कम (रुपये)</th>
                    </tr>
                    <tr className="bg-gray-50/50">
                      <th className="border border-black p-2">वहीती क्षेत्र (हे.आ.)</th>
                      <th className="border border-black p-2">पो.ख. क्षेत्र (हे.आ.)</th>
                      <th className="border border-black p-2">एकूण क्षेत्र (हे.आ.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-black p-2 font-medium">
                        {owner.village_name || "-"}, ता.{owner.taluka_name || "-"}, जि.{owner.dist_name || "-"}
                      </td>
                      <td className="border border-black p-2">{owner.survey_no || "-"}</td>
                      <td className="border border-black p-2">{aqrArea.toFixed(2)}</td>
                      <td className="border border-black p-2">{potKharab.toFixed(2)}</td>
                      <td className="border border-black p-2">{totalArea.toFixed(2)}</td>
                      <td className="border border-black p-2 font-semibold">{formatCurrency(owner.total_compensation)}</td>
                    </tr>
                  </tbody>
                </table>

                <p className="text-[15px] mb-10">
                  (अक्षरी- {amountInWords} फक्त) 
                </p>

                <div className="flex justify-end mb-12">
                  <div className="text-center">
                    <div className="mb-8">भूधारकाची सही-</div>
                    <div className="">{owner.owner_name}</div>
                  </div>
                </div>

                <div className="mb-6 font-medium text-[15px]">
                  दिनांक : {owner.namuna_k_date ? formatDate(owner.namuna_k_date) : "_____________"}
                </div>

                <div className="flex justify-between items-start mt-8">
                  <div>
                    <div className=" mb-6 text-[15px]">साक्षीदार :-</div>
                    <div className="mb-4">1) {owner.namuna_k_witness1 || "…………………………………………"}</div>
                    <div className="mb-4">2) {owner.namuna_k_witness2 || "…………………………………………"}</div>
                  </div>
                  <div className="text-center pt-6">
                    <div className=" mb-10 text-[15px]">समक्ष -</div>
                    <div className="">({laoName || "------------------"})</div>
                    <div className=" mt-1">भूसंपादन अधिकारी</div> 
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center text-gray-500 py-20 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="text-xl">कोणताही डेटा उपलब्ध नाही (No records found)</div>
          </div>
        )}
      </div>

      {/* ===================== EDIT MODAL ===================== */}
      {openModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          {/* ADDED: max-h-[90vh] overflow-y-auto to enable scrolling */}
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6 border-b pb-4 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-800">
                नमुना - क संपादित करा (Edit Namuna K)
              </h2>
              <button onClick={() => setOpenModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X size={22} className="text-gray-600" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2">
                <label className="block font-semibold text-gray-700 mb-1">मालक / Owner</label>
                <input
                  value={formData.owner_name}
                  disabled
                  className="w-full border border-gray-300 p-2.5 rounded bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">दिनांक (Date)</label>
                <input
                  type="date"
                  value={formData.namuna_k_date}
                  onChange={(e) => setFormData({ ...formData, namuna_k_date: e.target.value })}
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Textarea for editable content */}
              <div className="col-span-1 md:col-span-2">
                <label className="block font-semibold text-gray-700 mb-1">नमुना - क मजकूर (Receipt Content)</label>
                <textarea
                  value={formData.namuna_k_default_content}
                  onChange={(e) => setFormData({ ...formData, namuna_k_default_content: e.target.value })}
                  rows={5}
                  placeholder="नमुना क चा मजकूर येथे प्रविष्ट करा..."
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500 outline-none resize-none text-[14px] leading-relaxed"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">साक्षीदार १ (Witness 1)</label>
                <input
                  value={formData.namuna_k_witness1}
                  onChange={(e) => setFormData({ ...formData, namuna_k_witness1: e.target.value })}
                  placeholder="साक्षीदार १ चे नाव"
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">साक्षीदार २ (Witness 2)</label>
                <input
                  value={formData.namuna_k_witness2}
                  onChange={(e) => setFormData({ ...formData, namuna_k_witness2: e.target.value })}
                  placeholder="साक्षीदार २ चे नाव"
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8 border-t pt-5">
              <button
                onClick={() => setOpenModal(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                रद्द करा (Cancel)
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-md"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin h-4 w-4" />
                    जतन करत आहे...
                  </>
                ) : (
                  <>
                    <Save size={18} /> जतन करा (Save)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormC;